import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export interface UserActivity {
  id: string;
  userId: string;
  userName: string;
  type: 'page_visit' | 'protein_exchange' | 'room_reservation' | 'reception_appointment' | 'post_creation' | 'comment' | 'reaction' | 'equipment_request' | 'birthday_comment';
  description: string;
  points: number;
  timestamp: Date;
  metadata?: Record<string, unknown>;
}

export interface UserStats {
  userId: string;
  userName: string;
  userSector: string;
  userAvatar: string;
  totalPoints: number;
  level: number;
  activities: UserActivity[];
  badges: string[];
  streak: number;
  lastActivity: Date;
  totalActions: number;
}

interface GamificationContextType {
  userStats: UserStats | null;
  allUsersStats: UserStats[];
  addActivity: (
    type: UserActivity['type'],
    description: string,
    metadata?: Record<string, unknown>
  ) => Promise<void>;
  removeActivity: (
    type: UserActivity['type'],
    metadata?: Record<string, unknown>
  ) => Promise<void>;
  getUserRank: (userId: string) => number;
  getTopUsers: (limit?: number) => UserStats[];
  getTotalActivities: () => number;
  getActivityByType: (type: UserActivity['type']) => number;
  refreshStats: () => Promise<void>;
}

const GamificationContext = createContext<GamificationContextType | undefined>(undefined);

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (context === undefined) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

interface GamificationProviderProps {
  children: ReactNode;
}

const POINT_VALUES = {
  page_visit: 1,
  protein_exchange: 1,
  room_reservation: 5,
  reception_appointment: 5,
  post_creation: 15,
  comment: 3,
  reaction: 2,
  equipment_request: 6,
  birthday_comment: 2,
};

export const GamificationProvider: React.FC<GamificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [allUsersStats, setAllUsersStats] = useState<UserStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadAllUsersStats = useCallback(async () => {
    if (isLoading) {
      console.log('[GAMIFICATION] Already loading, skipping...');
      return;
    }

    try {
      setIsLoading(true);
      console.log('[GAMIFICATION] Loading stats from database...');

      const { data: statsData, error: statsError } = await supabase
        .from('gamification_user_stats')
        .select(`
          usuario_id,
          total_pontos,
          nivel,
          total_acoes,
          streak,
          ultima_atividade,
          badges,
          usuarios!inner (
            nome,
            setor,
            picture,
            ativo
          )
        `)
        .eq('usuarios.ativo', true)
        .order('total_pontos', { ascending: false });

      if (statsError) {
        console.error('[GAMIFICATION] Error loading stats:', statsError);
        setAllUsersStats([]);
        setUserStats(null);
        return;
      }

      console.log('[GAMIFICATION] Stats loaded:', statsData?.length || 0, 'users');

      const { data: activitiesData, error: activitiesError } = await supabase
        .from('gamification_activities')
        .select('*')
        .order('created_at', { ascending: false });

      if (activitiesError) {
        console.error('[GAMIFICATION] Error loading activities:', activitiesError);
        setAllUsersStats([]);
        setUserStats(null);
        return;
      }

      console.log('[GAMIFICATION] Activities loaded:', activitiesData?.length || 0, 'activities');

      const transformedStats: UserStats[] = (statsData || []).map((stat: any) => {
        const userActivities = (activitiesData || [])
          .filter((act: any) => act.usuario_id === stat.usuario_id)
          .map((act: any) => ({
            id: act.id,
            userId: act.usuario_id,
            userName: stat.usuarios?.nome || 'Usuário',
            type: act.tipo,
            description: act.descricao,
            points: act.pontos,
            timestamp: new Date(act.created_at),
            metadata: act.metadata || {},
          }));

        return {
          userId: stat.usuario_id,
          userName: stat.usuarios?.nome || 'Usuário',
          userSector: stat.usuarios?.setor || 'Geral',
          userAvatar: stat.usuarios?.picture || 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?w=150',
          totalPoints: stat.total_pontos || 0,
          level: stat.nivel || 1,
          activities: userActivities,
          badges: stat.badges || [],
          streak: stat.streak || 0,
          lastActivity: new Date(stat.ultima_atividade || new Date()),
          totalActions: stat.total_acoes || 0,
        };
      });

      setAllUsersStats(transformedStats);

      if (user?.dbId) {
        const currentUserStats = transformedStats.find(s => s.userId === user.dbId);
        if (currentUserStats) {
          console.log('[GAMIFICATION] Current user stats:', {
            userId: currentUserStats.userId,
            userName: currentUserStats.userName,
            totalPoints: currentUserStats.totalPoints,
            level: currentUserStats.level,
            totalActions: currentUserStats.totalActions
          });
          setUserStats(currentUserStats);
        } else {
          console.log('[GAMIFICATION] No stats found for current user, user.dbId:', user.dbId);
          setUserStats(null);
        }
      }
    } catch (error) {
      console.error('[GAMIFICATION] Error in loadAllUsersStats:', error);
      setAllUsersStats([]);
      setUserStats(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.dbId, isLoading]);

  const addActivity = useCallback(
    async (
      type: UserActivity['type'],
      description: string,
      metadata?: Record<string, unknown>
    ) => {
      if (!user?.dbId) {
        console.warn('[GAMIFICATION] Cannot add activity: user not found', {
          hasUser: !!user,
          userId: user?.id,
          dbId: user?.dbId
        });
        return;
      }

      const points = POINT_VALUES[type] || 1;

      try {
        // Prevent duplicate birthday_comment points per person per month
        if (type === 'birthday_comment' && metadata?.birthday_person) {
          const now = new Date();
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

          const { data: existing } = await supabase
            .from('gamification_activities')
            .select('id')
            .eq('usuario_id', user.dbId)
            .eq('tipo', 'birthday_comment')
            .gte('created_at', monthStart)
            .lte('created_at', monthEnd)
            .contains('metadata', { birthday_person: metadata.birthday_person })
            .limit(1);

          if (existing && existing.length > 0) {
            console.log('[GAMIFICATION] Duplicate birthday_comment blocked for:', metadata.birthday_person);
            return;
          }
        }

        // Prevent duplicate comment points per post
        if (type === 'comment' && metadata?.postId) {
          const { data: existing } = await supabase
            .from('gamification_activities')
            .select('id')
            .eq('usuario_id', user.dbId)
            .eq('tipo', 'comment')
            .contains('metadata', { postId: metadata.postId })
            .limit(1);

          if (existing && existing.length > 0) {
            console.log('[GAMIFICATION] Duplicate comment blocked for post:', metadata.postId);
            return;
          }
        }

        const { data, error } = await supabase
          .from('gamification_activities')
          .insert({
            usuario_id: user.dbId,
            tipo: type,
            descricao: description,
            pontos: points,
            metadata: metadata || {},
          })
          .select();

        if (error) {
          console.error('[GAMIFICATION] Error adding activity:', error);
          return;
        }

        await loadAllUsersStats();
      } catch (error) {
        console.error('[GAMIFICATION] Error in addActivity:', error);
      }
    },
    [user?.dbId, loadAllUsersStats]
  );

  const removeActivity = useCallback(
    async (
      type: UserActivity['type'],
      metadata?: Record<string, unknown>
    ) => {
      if (!user?.dbId) {
        console.warn('[GAMIFICATION] Cannot remove activity: user not found');
        return;
      }

      const points = -(POINT_VALUES[type] || 1);

      try {
        console.log('[GAMIFICATION] Removing activity (negative points):', {
          usuario_id: user.dbId,
          tipo: type,
          pontos: points
        });

        const { data, error } = await supabase
          .from('gamification_activities')
          .insert({
            usuario_id: user.dbId,
            tipo: type,
            descricao: `Removeu ${type}`,
            pontos: points,
            metadata: metadata || {},
          })
          .select();

        if (error) {
          console.error('[GAMIFICATION] Error removing activity:', error);
          return;
        }

        console.log('[GAMIFICATION] Activity removed successfully:', data);
        await loadAllUsersStats();
      } catch (error) {
        console.error('[GAMIFICATION] Error in removeActivity:', error);
      }
    },
    [user?.dbId, loadAllUsersStats]
  );

  const refreshStats = useCallback(async () => {
    await loadAllUsersStats();
  }, [loadAllUsersStats]);

  useEffect(() => {
    if (user?.dbId && !isLoading) {
      console.log('[GAMIFICATION] User changed, loading stats for:', user.dbId);
      loadAllUsersStats();
    }
  }, [user?.dbId]);

  const getUserRank = useCallback((userId: string): number => {
    const sortedUsers = [...allUsersStats].sort((a, b) => b.totalPoints - a.totalPoints);
    return sortedUsers.findIndex(u => u.userId === userId) + 1;
  }, [allUsersStats]);

  const getTopUsers = useCallback((limit: number = 10): UserStats[] => {
    return [...allUsersStats]
      .sort((a, b) => b.totalPoints - a.totalPoints)
      .slice(0, limit);
  }, [allUsersStats]);

  const getTotalActivities = useCallback((): number => {
    return allUsersStats.reduce((total, user) => total + user.activities.length, 0);
  }, [allUsersStats]);

  const getActivityByType = useCallback((type: UserActivity['type']): number => {
    return allUsersStats.reduce((total, user) => {
      return total + user.activities.filter(activity => activity.type === type).length;
    }, 0);
  }, [allUsersStats]);

  return (
    <GamificationContext.Provider
      value={{
        userStats,
        allUsersStats,
        addActivity,
        removeActivity,
        getUserRank,
        getTopUsers,
        getTotalActivities,
        getActivityByType,
        refreshStats,
      }}
    >
      {children}
    </GamificationContext.Provider>
  );
};
